<template>
    <div class="modal">
        <div class="modal-content">
            <h3>Meeting Check-In</h3>
            <div class="member-info">
                <p><strong>{{ member.first_name }} {{ member.last_name }}</strong></p>
                <p class="event-title">{{ event.event_type }}</p>
            </div>

            <div class="attendance-toggle">
                <label>
                    <input 
                        type="checkbox" 
                        v-model="checkIn.attending"
                    >
                    I will attend this meeting
                </label>
            </div>

            <!-- Show substitute section only if not attending -->
            <div v-if="!checkIn.attending" class="substitute-section">
                <label>
                    <input 
                        type="checkbox" 
                        v-model="checkIn.has_substitute"
                    >
                    I am sending a substitute
                </label>
                <div v-if="checkIn.has_substitute" class="substitute-input">
                    <input 
                        type="text"
                        v-model="checkIn.substitute_name"
                        placeholder="Enter substitute's name..."
                        class="substitute-name"
                    >
                </div>
            </div>

            <!-- Visitors Section - always visible -->
            <div class="visitors-section">
                <label>
                    <input 
                        type="checkbox" 
                        v-model="checkIn.has_visitors"
                    >
                    I am bringing visitors
                </label>
                <div v-if="checkIn.has_visitors" class="visitor-input">
                    <textarea 
                        v-model="checkIn.visitor_names"
                        placeholder="Enter visitor names (one per line)..."
                        class="visitor-names"
                        rows="3"
                    ></textarea>
                </div>
            </div>

            <div class="check-in-form">
                <div v-if="!allScoresSet" class="scores-warning">
                    Please set all scores before checking in
                </div>
                <!-- Business Score -->
                <div class="score-section">
                    <label>How is your business going?</label>
                    <div class="score-slider">
                        <input 
                            type="range" 
                            v-model="checkIn.business_score" 
                            min="0" 
                            max="10" 
                            step="1"
                        >
                        <span class="score-value">{{ checkIn.business_score }}/10</span>
                    </div>
                </div>

                <!-- Personal Score -->
                <div class="score-section">
                    <label>How is your personal life?</label>
                    <div class="score-slider">
                        <input 
                            type="range" 
                            v-model="checkIn.personal_score" 
                            min="0" 
                            max="10" 
                            step="1"
                        >
                        <span class="score-value">{{ checkIn.personal_score }}/10</span>
                    </div>
                </div>

                <!-- Health Score -->
                <div class="score-section">
                    <label>How is your health?</label>
                    <div class="score-slider">
                        <input 
                            type="range" 
                            v-model="checkIn.health_score" 
                            min="0" 
                            max="10" 
                            step="1"
                        >
                        <span class="score-value">{{ checkIn.health_score }}/10</span>
                    </div>
                </div>

                <!-- Comments -->
                <div class="comments-section">
                    <label>Additional Comments (Optional)</label>
                    <textarea 
                        v-model="checkIn.comments"
                        rows="3"
                        placeholder="Any comments you'd like to share..."
                    ></textarea>
                </div>
            </div>

            <div class="modal-actions">
                <button @click="$emit('close')" class="cancel-btn">Cancel</button>
                <button 
                    @click="submitCheckIn" 
                    class="submit-btn" 
                    :disabled="isSubmitting || !allScoresSet"
                    :class="{ 'disabled': !allScoresSet }"
                >
                    {{ isSubmitting ? 'Submitting...' : submitButtonText }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { checkinService } from '../services/checkinService';

export default {
    name: 'TokenCheckInModal',
    emits: ['close', 'check-in'],
    props: {
        token: {
            type: String,
            required: true
        },
        event: {
            type: Object,
            required: true
        },
        member: {
            type: Object,
            required: true
        },
        existingCheckIn: {
            type: Object,
            default: null
        }
    },
    setup(props, { emit }) {
        const checkIn = ref({
            business_score: props.existingCheckIn?.business_score ?? 0,
            personal_score: props.existingCheckIn?.personal_score ?? 0,
            health_score: props.existingCheckIn?.health_score ?? 0,
            comments: props.existingCheckIn?.comments ?? '',
            attending: props.existingCheckIn?.attending ?? true,
            has_visitors: props.existingCheckIn?.has_visitors ?? false,
            visitor_names: props.existingCheckIn?.visitor_names ?? '',
            has_substitute: props.existingCheckIn?.has_substitute ?? false,
            substitute_name: props.existingCheckIn?.substitute_name ?? ''
        });

        const isSubmitting = ref(false);

        const submitCheckIn = async () => {
            if (isSubmitting.value) return;
            isSubmitting.value = true;
            
            try {
                await checkinService.submitCheckinWithToken(props.token, {
                    attending: checkIn.value.attending,
                    business_score: parseInt(checkIn.value.business_score),
                    personal_score: parseInt(checkIn.value.personal_score),
                    health_score: parseInt(checkIn.value.health_score),
                    comments: checkIn.value.comments,
                    has_visitors: checkIn.value.has_visitors,
                    visitor_names: checkIn.value.visitor_names,
                    has_substitute: checkIn.value.has_substitute,
                    substitute_name: checkIn.value.substitute_name
                });

                emit('check-in');
                emit('close');
            } catch (error) {
                console.error('Error submitting check-in:', error);
                alert('Failed to submit check-in. Please try again.');
            } finally {
                isSubmitting.value = false;
            }
        };

        const updateSliderBackground = (e) => {
            const target = e.target;
            const value = (target.value - target.min) / (target.max - target.min) * 100;
            target.style.setProperty('--value', `${value}%`);
        };

        // Watch for changes in attendance status
        watch(() => checkIn.value.attending, (isAttending) => {
            if (isAttending) {
                // Clear substitute when switching to attending
                checkIn.value.has_substitute = false;
                checkIn.value.substitute_name = '';
            }
        });

        onMounted(() => {
            const sliders = document.querySelectorAll('input[type="range"]');
            sliders.forEach(slider => {
                updateSliderBackground({ target: slider });
                slider.addEventListener('input', updateSliderBackground);
            });
        });

        // Computed property to check if all scores have been set
        const allScoresSet = computed(() => {
            return checkIn.value.business_score > 0 && 
                   checkIn.value.health_score > 0 && 
                   checkIn.value.personal_score > 0;
        });

        // Update button text
        const submitButtonText = computed(() => {
            return props.existingCheckIn ? 'Update Check-in' : 'Check In';
        });

        return {
            checkIn,
            isSubmitting,
            submitCheckIn,
            allScoresSet,
            submitButtonText
        };
    }
};
</script>

<style scoped>
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
}

.member-info {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    text-align: center;
}

.member-info p {
    margin: 0.25rem 0;
}

.event-title {
    color: #6c757d;
    font-size: 0.9rem;
}

.score-section {
    margin-bottom: 1.5rem;
}

.score-section label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.score-slider {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.score-slider input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: #ddd;
    outline: none;
    padding: 0;
    margin: 0;
}

.score-slider input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #4285f4;
    cursor: pointer;
    margin-top: -6px;
}

.score-slider input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: 0;
    border-radius: 50%;
    background: #4285f4;
    cursor: pointer;
}

.score-slider input[type="range"]::-webkit-slider-runnable-track {
    width: 100%;
    height: 4px;
    cursor: pointer;
    background: linear-gradient(to right, #4285f4 var(--value), #ddd var(--value));
    border-radius: 2px;
    border: 0;
}

.score-slider input[type="range"]::-moz-range-track {
    width: 100%;
    height: 4px;
    cursor: pointer;
    background: #ddd;
    border-radius: 2px;
    border: 0;
}

.score-value {
    min-width: 3rem;
    text-align: right;
}

.comments-section {
    margin-top: 2rem;
}

.comments-section textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
}

.modal-actions {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.cancel-btn, .submit-btn {
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
}

.cancel-btn {
    background: #6c757d;
    color: white;
}

.cancel-btn:hover {
    background: #5a6268;
}

.submit-btn {
    background: #4285f4;
    color: white;
}

.submit-btn:hover:not(:disabled) {
    background: #357abd;
}

.submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.attendance-toggle {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 4px;
}

.attendance-toggle label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

.visitors-section,
.substitute-section {
    margin: 1rem 0;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.visitor-names,
.substitute-name {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
}

.visitor-names {
    resize: vertical;
}

label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #4285f4;
    border-radius: 3px;
    margin-right: 8px;
    position: relative;
    cursor: pointer;
    vertical-align: middle;
}

input[type="checkbox"]:checked {
    background-color: #4285f4;
}

input[type="checkbox"]:checked:after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.scores-warning {
    color: #dc3545;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    text-align: center;
}

.submit-btn.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #6c757d;
}

.substitute-input,
.visitor-input {
    margin-top: 0.5rem;
}
</style>